import React from "react";
import usePrevious from "../lib/hooks/usePrevious";
import useSelectablePoints from "./hooks/useSelectablePoints";
import usePrintableOptions from "./hooks/usePrintableOptions";
import useWrapperDimensions from "./hooks/useWrapperDimensions";
import BetterHighcharts from "./BetterHighcharts";
import Wrapper from "./ui/Wrapper";
import { stableColor } from "..";
import smartUpdate from "./lib/smartUpdate";
import { withNavigatorSeriesOptions } from "./lib/navigator";
import { preventSeriesDataError15 } from "./lib/preventSeriesDataError15";
import mergeDeep from "lodash.merge";

export type Options = BetterHighcharts.Options & {
  initial: BetterHighcharts.Options;
};

interface P {
  name: string;
  options: Options;
  className?: string;
  normalizeNavigatorSeries?: boolean;
  selectablePoints: boolean;
  timezone?: string | null;
  range?: boolean;
}
export const Highcharts = (
  {
    options: { initial: initialOptions, ...options },
    className,
    name,
    selectablePoints,
    normalizeNavigatorSeries = false,
    timezone = null,
    range = true,
  }: P,
  chartRef: React.Ref<BetterHighcharts.Chart>,
) => {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  if (!chartRef || !("current" in chartRef))
    throw new Error(
      "You must pass a ref object: <Highcharts ref={React.useRef<Highcharts.Chart>(null)} ... />",
    );
  let resetChart = false;
  [options, resetChart] = usePrintableOptions(options);

  const previousOptions = usePrevious(options);

  React.useLayoutEffect(() => {
    if (!wrapperRef.current) throw new Error(`wrapperRef empty ${name}`);
    if (previousOptions) {
      // UPDATE
      if (!chartRef.current) throw new Error(`chartRef empty ${name}`);
      // resetChart is DANGEROUS.
      // DO NOT USE ON "INTERACTIVE" GRAPHS.
      // It changes `chartRef.current` value, messing with all hoisted references in our hooks: `const chart = chartRef.current`
      if (resetChart) {
        console.debug(
          `%c[${name}] %RESET_UPDATE`,
          `color: ${stableColor(name)};`,
          "color: pink;",
          options,
        );
        chartRef.current.destroy();
        const chart = BetterHighcharts.chart(
          wrapperRef.current,
          withNavigatorSeriesOptions(
            Boolean(options.navigator?.enabled),
            options,
            normalizeNavigatorSeries,
          ),
        );
        chart.name = name;
        chart.timezone = timezone;
        (chartRef as React.MutableRefObject<BetterHighcharts.Chart>).current = chart;
      } else {
        smartUpdate(name, chartRef.current, options, previousOptions, normalizeNavigatorSeries);
      }
    } else {
      // CREATE
      const { series, ...otherOptions } = options;
      if (process.env["NODE_ENV"] === "development") {
        if (series) for (const serie of series) preventSeriesDataError15(serie);
      }
      const fullOptions = withNavigatorSeriesOptions(
        Boolean(options.navigator?.enabled),

        mergeDeep({ ...initialOptions, series }, otherOptions),
        normalizeNavigatorSeries,
      );
      const chart = BetterHighcharts.chart(wrapperRef.current, fullOptions);
      chart.name = name;
      (chartRef as React.MutableRefObject<BetterHighcharts.Chart>).current = chart;
      console.debug(
        `%c[${name}] %cCREATION`,
        `color: ${stableColor(name)};`,
        "color: blue;",
        { normalizeNavigatorSeries, selectablePoints },
        fullOptions,
      );
      window.setTimeout(() => {
        console.debug(
          `%c[${name}] %cMUTABLE INSTANCE CREATED`,
          `color: ${stableColor(name)};`,
          "color: blue;",
          chartRef.current,
        );
      });
    }
  }, [
    chartRef,
    initialOptions,
    name,
    normalizeNavigatorSeries,
    options,
    previousOptions,
    resetChart,
    selectablePoints,
    timezone,
  ]);

  useWrapperDimensions(wrapperRef, chartRef);
  useSelectablePoints(selectablePoints, wrapperRef, chartRef, range);

  // DESTROY
  React.useLayoutEffect(
    () => () => {
      console.debug(`%c[${name}] %cDESTROY`, `color: ${stableColor(name)};`, "color: black;");
      // we do not use Highcharts cascading destroy: slow and mostly useless
      // chart's DOM disappears when React removes it and then internals get garbage collected (no ref)
      // but absolute elements outside of the React app (on body) must be removed from the DOM
      chartRef.current?.tooltip?.destroy();
    },
    [chartRef, name],
  );

  return <Wrapper className={className} ref={wrapperRef} />;
};

export default React.memo(React.forwardRef<BetterHighcharts.Chart, P>(Highcharts));
